import React, { useEffect, useMemo, useState } from "react";
import TSFormUtils from "../../utils/helpers/TSFormUtils";
import ReactGoogleAutocomplete from "react-google-autocomplete";
import CONFIG from "../../model/TSConfig";
import { tt } from "../../utils/i18n/i18n";
import { COUNTRIES } from "../../utils/constants";
import { AxiosForLegacyBackend } from "../../utils/helpers/AxiosForLegacyBackend";
import { useDispatch } from "react-redux";
import { setShippingAddress } from "../../redux/actions/user";
import InputMask from "react-input-mask";
import KC_Button, {
  KC_ButtonType
} from "../../designsystem/KC_Button/KC_Button";
import KC_Span from "../../designsystem/KC_Span/KC_Span";
import KC_FormInput from "../../designsystem/KC_FormInput/KC_FormInput";
import type Address from "@tootsweet/model/entities/Address";

interface Props {
  trackingID: string;
  address: Address | undefined | null;
  setIsLoading: (loading: boolean) => void;
  ActionsComponent?: (extraProps?: any) => JSX.Element | null;
  onValidChanged?: (isValid: boolean) => void;
  blockCountry?: boolean;
}

export default function AddressEditor(props: Props) {
  const [address, setAddress] = useState<Address | null>(null);
  const [addressComponents, setAddressComponents] = useState<any>(null);

  const isValid = useMemo(() => {
    return addressIsValid(address);
  }, [address]);

  const dispatch = useDispatch();

  const onAddressSelected = (places: any) => {
    if (!places || !places.address_components) {
      return;
    }
    setAddressComponents(places.address_components);
  };

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (address == null) {
      alert(tt("api_error"));
      return;
    }
    await handleAddressSubmit(address, props.trackingID, props.setIsLoading);
    dispatch(setShippingAddress(props.trackingID, address));
  };

  const formattedAddress = useMemo(() => {
    if (address == null) return "";

    const arr: string[] = [];

    if (address?.number) {
      arr.push(address.number);
    }
    if (address?.line1) {
      arr.push(address.line1);
    }
    if (address?.postalCode) {
      arr.push(address.postalCode);
    }
    if (address?.city) {
      arr.push(address.city);
    }
    if (address?.country) {
      arr.push(address.country);
    }
    return arr.join(", ");
  }, [address]);

  useEffect(() => {
    if (props.address == null) {
      return;
    }
    setAddress(props.address);
  }, [props.address]);

  useEffect(() => {
    if (!addressComponents) {
      return;
    }

    try {
      const number = addressComponents.find(
        (c: any) => c.types && c.types.indexOf("street_number") !== -1
      );
      const route = addressComponents.find(
        (c: any) => c.types && c.types.indexOf("route") !== -1
      );
      const city = addressComponents.find(
        (c: any) => c.types && c.types.indexOf("locality") !== -1
      );
      const country = addressComponents.find(
        (c: any) => c.types && c.types.indexOf("country") !== -1
      );
      const postal_code = addressComponents.find(
        (c: any) => c.types && c.types.indexOf("postal_code") !== -1
      );
      const newAddress: Address = {
        ...address,
        number: number.long_name,
        line1: route.long_name,
        city: city.long_name,
        postalCode: postal_code.long_name,
        country: country.long_name,
        countryCode: country.short_name
      };
      setAddress(newAddress);
    } catch (err) {
      alert(tt("technical_error"));
    }
  }, [addressComponents]);

  useEffect(() => {
    props.onValidChanged?.(isValid);
  }, [isValid]);

  const { ActionsComponent } = props;

  return (
    <form onSubmit={onSubmit} autoComplete="off">
      {TSFormUtils.renderControl_3_9(
        tt("full_address") + "\u00A0*",
        <>
          <ReactGoogleAutocomplete
            id="delivery-addr"
            className="form-control"
            apiKey={CONFIG.googleAPIKey}
            onPlaceSelected={onAddressSelected}
            onChange={() => {
              setAddress({
                ...address,
                number: "",
                line1: "",
                city: "",
                postalCode: "",
                country: "",
                countryCode: ""
              });
            }}
            options={{
              fields: ["address_components", "name"],
              types: ["street_address", "street_number"],
              componentRestrictions: {
                country: COUNTRIES
              }
            }}
            placeholder={tt("placeholder_shipping_address")}
            inputAutocompleteValue="off"
            aria-autocomplete="none"
            defaultValue={formattedAddress}
          />
        </>
      )}
      {TSFormUtils.renderControl_3_9(
        tt("shipping_name") + "\u00A0*",
        <KC_FormInput
          id="delivery-name"
          className="form-control"
          value={address?.name}
          onChange={(e: any) => {
            setAddress({ ...address, name: e.target.value });
          }}
        />
      )}
      {TSFormUtils.renderControl_3_9(
        tt("shipping_phone_number") + "\u00A0*",
        <>
          <InputMask
            id="delivery-pn"
            mask={MASK}
            className="form-control"
            value={address?.phoneNumber}
            onChange={(e) => {
              setAddress({
                ...address,
                phoneNumber: e.target.value
              });
            }}
            placeholder={tt("phone_placeholder_french")}
            required={true}
            alwaysShowMask={false}
            maskPlaceholder=""
          />
          <KC_Span text={tt("deliverer_will_call")} className="d-block mt-2" />
        </>
      )}
      {ActionsComponent != null ? (
        <>
          <ActionsComponent
            extraProps={{ address, trackingID: props.trackingID }}
          />
        </>
      ) : (
        <>
          {TSFormUtils.renderControl_3_9(
            "",
            <KC_Button
              id={"delivery-action"}
              type={KC_ButtonType.Submit}
              disabled={!isValid}
              isPrimary
              text={tt("validate")}
            />
          )}
        </>
      )}
    </form>
  );
}

const MASK = [
  /0/,
  /[67]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/
];

const postalAddressIsValid = (address: Address | null): boolean => {
  return (
    !!address?.number &&
    !!address?.line1 &&
    !!address?.postalCode &&
    !!address?.city &&
    !!address?.country &&
    !!address?.countryCode
  );
};

const addressIsValid = (address: Address | null): boolean => {
  const res =
    postalAddressIsValid(address) &&
    !!address?.name &&
    !!address?.phoneNumber &&
    address?.phoneNumber?.length === 10;
  return res;
};

export async function handleAddressSubmit(
  address: Address,
  trackingID: string,
  setIsLoading?: (loading: boolean) => void
) {
  try {
    setIsLoading?.(true);
    await AxiosForLegacyBackend({
      method: "PUT",
      url: "/businessUsers/shipping_address",
      params: {
        trackingID
      },
      data: address
    });
  } catch (err) {
    console.error(err);
    alert(tt("api_error"));
  } finally {
    setIsLoading?.(false);
  }
}
